import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Lock, CheckCircle2, Clock, XCircle, MessageSquareWarning, ChevronDown, Send, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GATE_STATUS = {
  locked: { label: 'Locked', cls: 'bg-slate-100 dark:bg-slate-500/10 text-slate-500', icon: Lock },
  in_progress: { label: 'In Progress', cls: 'bg-blue-100 dark:bg-blue-500/10 text-blue-600', icon: Clock },
  submitted: { label: 'Awaiting Approval', cls: 'bg-amber-100 dark:bg-amber-500/10 text-amber-600', icon: Send },
  approved: { label: 'Approved', cls: 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-600', icon: CheckCircle2 },
  rejected: { label: 'Rejected', cls: 'bg-rose-100 dark:bg-rose-500/10 text-rose-600', icon: XCircle },
  changes_requested: { label: 'Changes Requested', cls: 'bg-violet-100 dark:bg-violet-500/10 text-violet-600', icon: MessageSquareWarning },
};

export const StageGates = ({ projectId }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const canApprove = ['super_admin', 'admin', 'manager'].includes(user?.role);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/gates`);
      setData(res.data);
      const active = res.data.gates.find(g => !['approved', 'locked'].includes(g.status));
      if (active) setExpanded(prev => prev ?? active.gate_number);
    } catch (e) {
      toast.error('Failed to load stage gates');
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const toggleItem = async (gate, item) => {
    try {
      await axios.put(`${API}/projects/${projectId}/gates/${gate.gate_number}/checklist/${item.id}`, { done: !item.done });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const submit = async (gate) => {
    setBusy(true);
    try {
      await axios.post(`${API}/projects/${projectId}/gates/${gate.gate_number}/submit`);
      toast.success(`Gate ${gate.gate_number} submitted for approval`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
    } finally { setBusy(false); }
  };

  const act = async (gate, action) => {
    setBusy(true);
    try {
      await axios.post(`${API}/projects/${projectId}/gates/${gate.gate_number}/action`, { action, comment });
      toast.success(`Gate ${action === 'approve' ? 'approved' : action.replace('_', ' ')}`);
      setComment('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally { setBusy(false); }
  };

  if (!data) return <div className="p-6 text-muted-foreground text-sm">Loading stage gates…</div>;

  const approvedCount = data.gates.filter(g => g.status === 'approved').length;

  return (
    <div className="space-y-4" data-testid="stage-gates">
      {/* Progress stepper */}
      <div className="flex items-center gap-1 flex-wrap" data-testid="gate-stepper">
        {data.gates.map((g, i) => {
          const meta = GATE_STATUS[g.status];
          return (
            <div key={g.gate_number} className="flex items-center">
              <button onClick={() => setExpanded(expanded === g.gate_number ? null : g.gate_number)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${meta.cls} ${expanded === g.gate_number ? 'ring-2 ring-primary/40' : ''}`}
                data-testid={`gate-step-${g.gate_number}`}>
                <meta.icon className="h-3.5 w-3.5" /> G{g.gate_number} {g.name}
              </button>
              {i < 4 && <div className={`h-0.5 w-4 ${i < approvedCount ? 'bg-emerald-400' : 'bg-border'}`} />}
            </div>
          );
        })}
      </div>

      {data.gates.map(gate => {
        if (expanded !== gate.gate_number) return null;
        const meta = GATE_STATUS[gate.status];
        const doneCount = gate.checklist.filter(i => i.done).length;
        const allDone = gate.checklist.every(i => !i.required || i.done);
        const editable = !['locked', 'approved', 'submitted'].includes(gate.status);
        return (
          <div key={gate.gate_number} className="border rounded-2xl p-4 space-y-3" data-testid={`gate-panel-${gate.gate_number}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h3 className="font-semibold flex items-center gap-2">
                  Gate {gate.gate_number} — {gate.name}
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">{doneCount}/{gate.checklist.length} checklist items complete
                  {gate.approved_by ? ` · Approved by ${gate.approved_by}` : ''}</p>
              </div>
              <div className="flex gap-2">
                {editable && (
                  <Button size="sm" disabled={!allDone || busy} onClick={() => submit(gate)} data-testid={`gate-submit-${gate.gate_number}`}>
                    <Send className="h-3.5 w-3.5 mr-1" /> Submit for Approval
                  </Button>
                )}
              </div>
            </div>

            {gate.status === 'locked' ? (
              <p className="text-sm text-muted-foreground flex items-center gap-2"><Lock className="h-4 w-4" /> Locked — the previous gate must be approved first.</p>
            ) : (
              <div className="space-y-1.5">
                {gate.checklist.map(item => (
                  <label key={item.id} className={`flex items-start gap-2 text-sm p-2 rounded-lg ${editable ? 'cursor-pointer hover:bg-muted/50' : 'opacity-80'}`}
                    data-testid={`gate-check-${gate.gate_number}-${item.id}`}>
                    <input type="checkbox" checked={item.done} disabled={!editable} onChange={() => toggleItem(gate, item)}
                      className="mt-0.5 h-4 w-4 rounded accent-blue-600" />
                    <span className={item.done ? 'line-through text-muted-foreground' : ''}>
                      {item.item}
                      {item.done && item.done_by && <span className="text-xs text-muted-foreground ml-1">— {item.done_by}</span>}
                    </span>
                  </label>
                ))}
              </div>
            )}

            {gate.status === 'submitted' && canApprove && (
              <div className="border-t pt-3 space-y-2">
                <Textarea placeholder="Approval comment (optional)" value={comment} onChange={e => setComment(e.target.value)}
                  className="text-sm" data-testid={`gate-comment-${gate.gate_number}`} />
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => act(gate, 'approve')} data-testid={`gate-approve-${gate.gate_number}`}>
                    <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Approve Gate
                  </Button>
                  <Button size="sm" variant="outline" className="text-rose-600" disabled={busy} onClick={() => act(gate, 'reject')} data-testid={`gate-reject-${gate.gate_number}`}>
                    <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                  </Button>
                  <Button size="sm" variant="outline" className="text-violet-600" disabled={busy} onClick={() => act(gate, 'request_changes')} data-testid={`gate-changes-${gate.gate_number}`}>
                    <MessageSquareWarning className="h-3.5 w-3.5 mr-1" /> Request Changes
                  </Button>
                </div>
              </div>
            )}

            {gate.history?.length > 0 && (
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer flex items-center gap-1"><ChevronDown className="h-3 w-3" /> History ({gate.history.length})</summary>
                <div className="mt-2 space-y-1 pl-4">
                  {gate.history.map((h, i) => (
                    <p key={i}><b className="capitalize">{h.action.replace('_', ' ')}</b> by {h.by} · {new Date(h.timestamp).toLocaleString('en-ZA')}{h.comment && ` — "${h.comment}"`}</p>
                  ))}
                </div>
              </details>
            )}
          </div>
        );
      })}
    </div>
  );
};
